import React, { useState, useEffect, useMemo } from 'react'
import { ModelPerformance } from '../../types/ai'
import ModelComparisonRadar from '../../components/charts/ModelComparisonRadar'
import ErrorDistributionChart from '../../components/charts/ErrorDistributionChart'
import AccuracyTrendChart from '../../components/charts/AccuracyTrendChart'

const Performance: React.FC = () => {
  const [models, setModels] = useState<ModelPerformance[]>([])
  const [selectedModel, setSelectedModel] = useState('LSTM')
  const [accuracyTrend, setAccuracyTrend] = useState<number[]>([])
  const [errorDistribution, setErrorDistribution] = useState<number[]>([])

  const mockModels = useMemo(() => [
    {
      model_name: 'LSTM',
      accuracy: 94.2,
      rmse: 5.8,
      mae: 4.2,
      r_squared: 0.89,
      last_trained: '2025-01-07T10:30:00Z',
      status: 'active'
    },
    {
      model_name: 'Prophet',
      accuracy: 91.5,
      rmse: 7.2,
      mae: 5.1,
      r_squared: 0.84,
      last_trained: '2025-01-06T15:20:00Z',
      status: 'active'
    },
    {
      model_name: 'Random Forest',
      accuracy: 88.7,
      rmse: 8.9,
      mae: 6.8,
      r_squared: 0.79,
      last_trained: '2025-01-05T09:15:00Z',
      status: 'inactive'
    },
    {
      model_name: 'ARIMA',
      accuracy: 85.3,
      rmse: 11.2,
      mae: 8.5,
      r_squared: 0.72,
      last_trained: '2025-01-04T14:45:00Z',
      status: 'inactive'
    }
  ], []);

  const mockAccuracyTrend = useMemo(() => 
    Array.from({ length: 30 }, (_, i) => 88 + i * 0.2 + (i % 3) * 2), []);

  const mockErrorDistribution = useMemo(() => 
    Array.from({ length: 100 }, (_, i) => 2 + i * 0.1 + Math.sin(i * 0.1) * 3), []);

  useEffect(() => {
    setModels(mockModels)
    setAccuracyTrend(mockAccuracyTrend)
    setErrorDistribution(mockErrorDistribution)
  }, [mockModels, mockAccuracyTrend, mockErrorDistribution])

  const bestModel = models.reduce((best, current) => 
    current.accuracy > best.accuracy ? current : best, models[0] || {} as ModelPerformance
  )



  const handleRetrain = async (modelName: string) => {
    // Mock retrain functionality
    console.log(`Retraining ${modelName}...`)
    // In real implementation, this would call the API
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">AI Model Performance</h1>
        <div className="flex gap-4">
          <select 
            value={selectedModel} 
            onChange={(e) => setSelectedModel(e.target.value)}
            className="px-4 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
          >
            {models.map(model => (
              <option key={model.model_name} value={model.model_name}>
                {model.model_name}
              </option>
            ))}
          </select>
          <button 
            onClick={() => handleRetrain(selectedModel)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retrain Model
          </button>
        </div>
      </div>

      {/* Performance Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
          <h3 className="text-sm font-semibold text-slate-600 mb-2">Best Model</h3>
          <p className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent">
            {bestModel?.model_name}
          </p>
          <p className="text-sm text-slate-500">{bestModel?.accuracy?.toFixed(1)}% accuracy</p>
        </div>
        <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
          <h3 className="text-sm font-semibold text-slate-600 mb-2">Avg Accuracy</h3>
          <p className="text-3xl font-bold bg-gradient-to-r from-green-600 to-green-500 bg-clip-text text-transparent">
            {(models.reduce((sum, m) => sum + m.accuracy, 0) / models.length).toFixed(1)}%
          </p>
        </div>
        <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
          <h3 className="text-sm font-semibold text-slate-600 mb-2">Active Models</h3>
          <p className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-purple-500 bg-clip-text text-transparent">
            {models.filter(m => m.status === 'active').length}
          </p>
        </div>
        <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
          <h3 className="text-sm font-semibold text-slate-600 mb-2">Last Retrain</h3>
          <p className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-orange-500 bg-clip-text text-transparent">
            {new Date(bestModel?.last_trained || '').toLocaleDateString()}
          </p>
        </div>
      </div>

      {/* Model Comparison Radar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
          <h3 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-6">
            Model Comparison 🎯
          </h3>
          <ModelComparisonRadar models={models} />
        </div>

        <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
          <h3 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-6">
            Error Distribution 📊
          </h3>
          <ErrorDistributionChart data={errorDistribution} />
        </div>
      </div>

      {/* Accuracy Trend */}
      <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
        <h3 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-6">
          Accuracy Trend (Last 30 Days) 📈
        </h3>
        <AccuracyTrendChart data={accuracyTrend} />
        <p className="text-sm text-slate-500 mt-2">30-day performance history</p>
      </div>

      {/* Model Registry */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Model Registry</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Model
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Accuracy
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  RMSE
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  R²
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {models.map((model, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {model.model_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {model.accuracy.toFixed(1)}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {model.rmse.toFixed(1)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {model.r_squared.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      model.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : model.status === 'training'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {model.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex gap-2">
                      <button 
                        onClick={() => handleRetrain(model.model_name)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Retrain
                      </button>
                      {model.status === 'inactive' && (
                        <button className="text-green-600 hover:text-green-900">
                          Deploy
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default Performance